import * as React from "react";
import { useState } from "react";
import { initializeIcons, Icon } from '@fluentui/react';
import { Button, Datepicker, DayOfWeek, Divider, Dropdown, Flex, Label, Input, Popup, Pill, Text, Toolbar, Tooltip } from "@fluentui/react-northstar";
import { CalendarIcon, ChevronDownMediumIcon, CloseIcon, MeetingTimeIcon, FilterIcon, SearchIcon, ExpandIcon } from '@fluentui/react-icons-northstar';
import { withTranslation, WithTranslation } from "react-i18next";
import { ImageUtil } from '../../../helpers/avatar-helper';
import Data from '../../../constants/data';
import Features from '../../../constants/features';
import Roles from "../../../constants/roles";
import StatusQueueConstants from "../../../constants/status-queue";

const moment = require('moment');

interface IToolbarProps extends WithTranslation {
    ancora: string,
    status: number,
    setActiveTabId: Function,
    queueCount1: any,
    queueCount2: any,
    queueCount3: any,
    queueCount4: any,
    agendar: Function,
    isScheduledQueue: boolean,
    dataChange: Function,
    selectedDateFilter: Date,
    selectedDateLabel: string,
    isFuture: boolean,
    isPast: boolean,
    searchFilters: any,
    selectedFilter: string,
    clearFilters: Function,
    applyFilters: Function,
    applyProtocolFilter: Function,
    atendentes: any,
    getAtendentesByDate: Function,
    selectedAtendenteFilter: string,
    role: string,
    isAdmin: boolean,
    collapseCharts: Function;
    collapsed: boolean;
    features: string[];
    isTeamContext?: boolean;
    searchQuery?: string | null;
}

const ToolbarPainel: React.FunctionComponent<IToolbarProps> = props => {
    initializeIcons();
    const localize = props.t;
    const localizationStrings = {
        shortDays: props.i18n.language.includes("es") ? Data.shortDaysES : props.i18n.language.includes("en") ? Data.shortDaysEN : Data.shortDaysPT,
        months: props.i18n.language.includes("es") ? Data.monthFullNamesES : props.i18n.language.includes("en") ? Data.monthFullNamesEN : Data.monthFullNames,
    }

    const [filtersOpen, setProtocolFiltersOpen] = useState(false);
    const [protocolFilteredView, setProtocolFilter] = useState(false);
    const [dateFilter, setDateFilter] = useState(props.selectedDateFilter);
    const [dateLabel, setDateLabel] = useState(localize("Hoje"));
    const [isPast, setPast] = useState(props.isPast);
    const [isFuture, setFuture] = useState(props.isFuture);

    const [selectedAtendente, setSelectedAtendente] = useState<any>([]);
    const [selectedAtendenteId, setSelectedAtendenteId] = useState(null);
    const [loading, setLoading] = useState(false);

    const onGroupSearch = (itemList: any, searchQuery: string) => {
        const result = itemList.filter(
            (item: { header: string; content: string; }) => (item.header && item.header.toLowerCase().indexOf(searchQuery.toLowerCase()) !== -1) ||
                (item.content && item.content.toLowerCase().indexOf(searchQuery.toLowerCase()) !== -1),
        )
        return result;
    }

    const getGroupItems = () => {
        if (props.atendentes.length) {
            return props.atendentes.map((atendente: any) => {
                return {
                    key: atendente.queue.atendenteId,
                    header: atendente.queue.atendenteName,
                    image: atendente.atendentePhoto ? `data:image/jpeg;base64,${atendente.atendentePhoto}` : ImageUtil.makeInitialImage(atendente.queue.atendenteName),
                }
            })
        }
    }

    const onAtendenteChange = (event: any, itemsData: any) => {
        if (itemsData.value.length > 1) {
            setSelectedAtendente(itemsData.value[1]);
            setSelectedAtendenteId(itemsData.value[1].key);
        } else {
            setSelectedAtendente(itemsData.value[0]);
            setSelectedAtendenteId(itemsData?.value.length ? itemsData.value[0].key : null);
        }
    }

    const filterContent = (
        <Flex className="filtros-painel" gap="gap.medium" column>
            <Flex gap="gap.large" vAlign="center">
                <Flex.Item align="center">
                    <Text size="large" weight="semibold" content="Filtros" />
                </Flex.Item>
                <Flex.Item push align="center">
                    <Button text iconOnly icon={<CloseIcon />} onClick={() => setProtocolFiltersOpen(false)} title="Close" />
                </Flex.Item>
            </Flex>

            <Divider />

            <Flex gap="gap.small" column>
                <Text size="small" weight="semibold" content={localize("Data")} />
                <Datepicker
                    {...localizationStrings}
                    firstDayOfWeek={DayOfWeek.Sunday}
                    today={dateFilter}
                    onDateChange={(e: any, date: any) => {
                        setDateFilter(date.value);
                        setProtocolFilter(false);
                        setLoading(true);
                        setSelectedAtendente([]);
                        setSelectedAtendenteId(null);
                        props.getAtendentesByDate(date.value).then((res: any) => setLoading(false));

                        if (moment(date.value).isSame(moment(), "day")) {
                            setDateLabel(localize("Hoje"));
                        } else {
                            setDateLabel(moment(date.value).format("DD/MM/YY"));
                        }
                    }}
                    selectedDate={dateFilter}
                    popup={{
                        trigger:
                            <Button
                                icon={<CalendarIcon outline />}
                                title={localize("Filtrar")}
                                content={dateLabel}
                            />,
                    }}
                />
            </Flex>

            <Divider />

            <Flex gap="gap.small" column>
                <Flex gap="gap.smaller" vAlign="center">
                    <Text size="small" weight="semibold" content={localize("Atendente")} />
                    <Tooltip
                        trigger={<Icon className="margin-style info-icon" iconName="Info" />}
                        content={localize("AtendenteUnidade")}
                    />
                </Flex>
                <Dropdown
                    multiple
                    fluid
                    className="hideToggle"
                    placeholder={localize("Pesquisar")}
                    loading={loading}
                    search={onGroupSearch}
                    loadingMessage={localize("Carregando")}
                    items={getGroupItems()}
                    value={selectedAtendente}
                    onChange={onAtendenteChange}
                    noResultsMessage={localize("NenhumaOcorrencia")}
                    unstable_pinned={true}
                />
            </Flex>

            <Divider />

            {props.isTeamContext &&
                <>
                    <Flex gap="gap.smaller" vAlign="center" column>
                        <Text size="small" weight="semibold" content={localize("Protocolo")} />
                        <Input
                            className="input-protocolo"
                            icon={
                                <SearchIcon />
                            }
                            placeholder={`Pesquisar assunto ou protocolo`}
                            onChange={props.searchFilters}
                            value={props.searchQuery}
                        />
                    </Flex>
                </>
            }

            <Flex vAlign="center">
                <Text className="clear-filter" content={localize("Limpar")} color="brand" onClick={() => {
                    props.clearFilters();
                    setProtocolFilter(false);
                    setDateFilter(new Date());
                    setSelectedAtendente([]);
                    setSelectedAtendenteId(null);
                    setDateLabel(localize("Hoje"));
                    setPast(false);
                    setFuture(false);
                    props.getAtendentesByDate(new Date()).then((res: any) => setLoading(false));
                }} />

                <Flex.Item push>
                    <Flex gap="gap.small">
                        <Button
                            content={localize("Cancelar")}
                            onClick={() => {
                                setProtocolFiltersOpen(false);
                                setProtocolFilter(false);

                            }}
                        />
                        <Button
                            primary
                            content={localize("Aplicar")}
                            onClick={() => {
                                setProtocolFilter(false);

                                if (moment(dateFilter).isSame(moment(), "day")) {
                                    setPast(false);
                                    setFuture(false);
                                } else {
                                    if (moment(dateFilter).isBefore(moment(), "day")) {
                                        setPast(true);
                                        setFuture(false);
                                    }

                                    if (moment(dateFilter).isAfter(moment(), "day")) {
                                        setPast(false);
                                        setFuture(true);
                                    }
                                }

                                props.applyFilters(dateFilter, selectedAtendenteId);
                            }}
                        />
                    </Flex>
                </Flex.Item>
            </Flex>
        </Flex>
    );

    return (
        <Flex gap="gap.medium" fill column>
            <Flex className="acesso-rapido-status">
                <Flex>
                    {!isPast &&
                        <React.Fragment>
                            <Flex
                                hidden={props.selectedAtendenteFilter != ""}
                                padding="padding.medium"
                                className={props.status == 1 ? "status-box active" : "status-box"}
                                onClick={() => props.setActiveTabId(1, `'${StatusQueueConstants.naFila}', '${StatusQueueConstants.atendimentoAgendado}'`)}
                            >
                                <Text
                                    className={props.status == 1 ? "statusQueue statusQueueActive" : "statusQueue"}
                                    size="small"
                                    weight={props.status == 1 ? 'semibold' : 'light'}
                                    content={localize("NovasSolicitacoes")}
                                />

                                <Label
                                    className={props.status == 1 ? "labelCount labelCountActive" : "labelCount"}
                                    circular
                                    content={`${props.queueCount1.length ? `(${Object.values(props.queueCount1).reduce((sum: any, item: any) => sum + item.count, 0)})` : `(0)`}`}
                                />
                            </Flex>
                        </React.Fragment>
                    }

                    {(!isPast && !isFuture) &&
                        <React.Fragment>
                            <Flex
                                padding="padding.medium"
                                className={props.status == 2 ? "status-box active" : "status-box"}
                                onClick={() => props.setActiveTabId(2, `'${StatusQueueConstants.atendimentoIniciado}', '${StatusQueueConstants.emAtendimento}'`)}
                            >
                                <Text
                                    className={props.status == 2 ? "statusQueue statusQueueActive" : "statusQueue"}
                                    size="small"
                                    weight={props.status == 2 ? 'semibold' : 'light'}
                                    content={localize("AtendimentosIniciados")}
                                />

                                <Label
                                    className={props.status == 2 ? "labelCount labelCountActive" : "labelCount"}
                                    circular
                                    content={`${props.queueCount2.length ? `(${Object.values(props.queueCount2).reduce((sum: any, item: any) => sum + item.count, 0)})` : `(0)`}`}
                                />
                            </Flex>
                        </React.Fragment>
                    }

                    {!isFuture &&
                        <React.Fragment>
                            <Flex
                                padding="padding.medium"
                                className={props.status == 3 ? "status-box active" : "status-box"}
                                onClick={() => props.setActiveTabId(3, `'${StatusQueueConstants.atendimentoEncerrado}', '${StatusQueueConstants.atendimentoInativo}', '${StatusQueueConstants.atendimentoCancelado}'`)}
                            >
                                <Text
                                    className={props.status == 3 ? "statusQueue statusQueueActive" : "statusQueue"}
                                    size="small"
                                    weight={props.status == 3 ? 'semibold' : 'light'}
                                    content={localize("AtendimentosFinalizados")}
                                />

                                <Label
                                    className={props.status == 3 ? "labelCount labelCountActive" : "labelCount"}
                                    circular
                                    content={`${props.queueCount3.length ? `(${Object.values(props.queueCount3).reduce((sum: any, item: any) => sum + item.count, 0)})` : `(0)`}`}
                                />
                            </Flex>
                            {!props.isTeamContext &&
                                <Flex
                                    hidden={props.selectedAtendenteFilter != ""}
                                    padding="padding.medium"
                                    className={props.status == 4 ? "status-box active" : "status-box"}
                                    onClick={() => props.setActiveTabId(4, `'${StatusQueueConstants.atendimentoInativo}'`)}
                                >
                                    <Text
                                        className={props.status == 4 ? "statusQueue statusQueueActive" : "statusQueue"}
                                        size="small"
                                        weight={props.status == 4 ? 'semibold' : 'light'}
                                        content={localize("SolicitacoesExpiradas")}
                                    />

                                    <Label
                                        className={props.status == 4 ? "labelCount labelCountActive" : "labelCount"}
                                        circular
                                        content={`${props.queueCount4.length ? `(${Object.values(props.queueCount4).reduce((sum: any, item: any) => sum + item.count, 0)})` : `(0)`}`}
                                    />
                                </Flex>
                            }
                        </React.Fragment>
                    }
                </Flex>
            </Flex>

            {!props.isAdmin && props.role == Roles.leitor ?
                null
                :
                <Flex space="between" style={{ boxShadow: "rgba(0 0 0 / 10%) 0px 0.2rem 0.4rem -0.075rem" }}>
                    <Flex gap="gap.small"
                        space="between"
                        className="toolbar-acesso-rapido"
                        styles={{
                            position: "sticky",
                            top: 0,
                            padding: "1.25rem 1.25rem 1.25rem 0",
                            margin: "1.25rem 0",
                            zIndex: 1,
                        }}>
                        <Datepicker
                            {...localizationStrings}
                            firstDayOfWeek={DayOfWeek.Sunday}
                            today={dateFilter}
                            onDateChange={(e: any, date: any) => {
                                setDateFilter(date.value);
                                setProtocolFilter(false);
                                setLoading(true);
                                setSelectedAtendente([]);
                                setSelectedAtendenteId(null);

                                if (moment(date.value).isSame(moment(), "day")) {
                                    setDateLabel(localize("Hoje"));
                                } else {
                                    setDateLabel(moment(date.value).format("DD/MM/YY"));
                                }

                                if (moment(date.value).isSame(moment(), "day")) {
                                    setPast(false);
                                    setFuture(false);
                                } else {
                                    if (moment(date.value).isBefore(moment(), "day")) {
                                        setPast(true);
                                        setFuture(false);
                                    }

                                    if (moment(date.value).isAfter(moment(), "day")) {
                                        setPast(false);
                                        setFuture(true);
                                    }
                                }

                                props.applyFilters(date.value, "");
                                props.getAtendentesByDate(date.value).then((res: any) => setLoading(false));
                            }}
                            selectedDate={dateFilter}
                            buttonOnly
                            popup={{
                                trigger:
                                    <Flex gap="gap.medium" vAlign="center" className="date-filter">
                                        <Text content={dateLabel} />
                                        <ChevronDownMediumIcon />
                                    </Flex>,
                            }}
                        />
                    </Flex>
                    <Flex gap="gap.medium" vAlign="center">
                        <Pill
                            actionable
                            appearance="outline"
                            className="filters"
                            hidden={!props.selectedAtendenteFilter}
                            content={`Atendente: ${selectedAtendente?.header}`}
                            onDismiss={(e, data) => {
                                // eslint-disable-next-line no-console
                                setProtocolFilter(false);
                                setSelectedAtendente([]);
                                setSelectedAtendenteId(null);
                                props.clearFilters();
                            }}
                        />

                        <Toolbar
                            items={[
                                {
                                    key: 'filters',
                                    kind: 'custom',
                                    content: <Popup
                                        open={filtersOpen}
                                        onOpenChange={(_e, props) => {
                                            const open = !!props?.open;
                                            setProtocolFiltersOpen(open);
                                        }}
                                        trigger={
                                            <Tooltip
                                                trigger={
                                                    <Button icon={<FilterIcon />} iconOnly text title={localize("Filtrar")} />
                                                }
                                                content={localize("Filtros")}
                                                position="below"
                                                align="center"
                                            />
                                        }
                                        content={filterContent}
                                        trapFocus
                                        align="end"
                                        position="below"
                                    />,
                                    fitted: 'horizontally',
                                },
                                {
                                    icon: (
                                        <ExpandIcon
                                            {...{
                                                outline: true,
                                            }}
                                        />
                                    ),
                                    key: 'collapse',
                                    active: !props.collapsed,
                                    className: "icon-only",
                                    onClick: () => props.collapseCharts(!props.collapsed),
                                },
                                {
                                    ...(props.features.includes(Features.ScheduledQueue) ?
                                        {
                                            key: 'schedule',
                                            kind: 'custom',
                                            content: <Tooltip
                                                trigger={
                                                    <Button
                                                        className="btn-agendamento"
                                                        hidden={!props.isScheduledQueue}
                                                        icon={<MeetingTimeIcon outline />}
                                                        text
                                                        iconOnly
                                                        onClick={() => { props.agendar() }}
                                                    />
                                                }
                                                content={localize("NovoAgendamento")}
                                                position="below"
                                                align="center"
                                            />,
                                            fitted: 'horizontally',
                                            hidden: !props.isScheduledQueue
                                        }
                                        :
                                        {
                                            key: 'none',
                                            className: 'empty',
                                        }
                                    )
                                },
                            ]}
                        />
                    </Flex>
                </Flex>
            }
        </Flex>
    );
};

const toolbarWithTranslation = withTranslation()(ToolbarPainel);
export default toolbarWithTranslation;